// MonthlyParkingReportTable.js
import React, { useState } from "react";
import { Table, Collapse, Button, Badge } from "reactstrap";
import PropTypes from "prop-types";
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";

const MonthlyParkingReportTable = ({ locations }) => {
    const [expanded, setExpanded] = useState({});
    const [page, setPage] = useState({});
    const itemsPerPage = 5;

    const toggleExpand = (key) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const paginate = (arr, key) => {
        const currentPage = page[key] || 1;
        const start = (currentPage - 1) * itemsPerPage;
        return arr.slice(start, start + itemsPerPage);
    };

    const totalPages = (arr) => Math.max(1, Math.ceil(arr.length / itemsPerPage));

    return (
        <div className="overflow-x-auto">
            <Table bordered responsive className="align-middle table-nowrap">
                <thead className="table-light">
                    <tr>
                        <th>Location</th>
                        <th>Completed Orders</th>
                        <th>Amount</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {locations.slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter(item => item.orders.length > 0).map((loc) => {
                            const completedOrders = loc.orders.filter(o => o.status === "completed");
                            const totalOrders = completedOrders.length;
                            const totalAmount = completedOrders.reduce((acc, o) => acc + (o.total || 0), 0);
                            const key = `loc-${loc.id}`;

                            return (
                                <React.Fragment key={loc.id}>
                                    <tr>
                                        <td>{loc.name}</td>
                                        <td>{totalOrders}</td>
                                        <td>${formatCurrency(totalAmount)}</td>
                                        <td>
                                            <Button
                                                className="button-gradient-bg"
                                                onClick={() => toggleExpand(key)}
                                            >
                                                {expanded[key] ? "Hide Orders" : "View Orders"}
                                            </Button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={4} className="p-0 border-0">
                                            <Collapse isOpen={expanded[key]}>
                                                <Table
                                                    bordered
                                                    size="sm"
                                                    responsive
                                                    className="mb-0 table-striped"
                                                >
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Customer</th>
                                                            <th>Amount</th>
                                                            <th>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paginate(loc.orders, key).map((o) => (
                                                            <tr key={o.id}>
                                                                <td>{formatDate(o.date_created)}</td>
                                                                <td>{(o.customer_first_name + " " + o.customer_last_name)}</td>
                                                                <td>
                                                                    {o.status === "completed"
                                                                        ? `$${formatCurrency(o.total)}`
                                                                        : "-"}
                                                                </td>
                                                                <td>
                                                                    <Badge
                                                                        color={
                                                                            o.status === "completed"
                                                                                ? "success"
                                                                                : o.status === "pending"
                                                                                    ? "warning"
                                                                                    : o.status === "refunded"
                                                                                        ? "info"
                                                                                        : "danger"
                                                                        }
                                                                    >
                                                                        {o.status}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                {loc.orders.length > itemsPerPage && (
                                                    <div className="d-flex justify-content-between align-items-center p-2">
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [key]: Math.max(1, (page[key] || 1) - 1),
                                                                }))
                                                            }
                                                            disabled={(page[key] || 1) === 1}
                                                        >
                                                            Previous
                                                        </Button>
                                                        <span>
                                                            Page {page[key] || 1} of {totalPages(loc.orders)}
                                                        </span>
                                                        <Button
                                                            color="secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setPage((prev) => ({
                                                                    ...prev,
                                                                    [key]: Math.min(
                                                                        totalPages(loc.orders),
                                                                        (page[key] || 1) + 1
                                                                    ),
                                                                }))
                                                            }
                                                            disabled={(page[key] || 1) === totalPages(loc.orders)}
                                                        >
                                                            Next
                                                        </Button>
                                                    </div>
                                                )}
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    {locations.length === 0 && (
                        <tr>
                            <td colSpan={4} className="text-center py-3">
                                No locations found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

MonthlyParkingReportTable.propTypes = {
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string.isRequired,
            orders: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    date_created: PropTypes.string.isRequired,
                    total: PropTypes.number,
                    status: PropTypes.string.isRequired,
                    customer_name: PropTypes.string.isRequired,
                })
            ),
        })
    ).isRequired,
};

export default MonthlyParkingReportTable;